<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { isWorkspaceV2Path } from 'dashboard/helper/workspaceV2';
import { worldById } from 'dashboard/routes/dashboard/hub/hubWorldLookup';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import {
  MEDIA_TYPES,
  NON_FILE_TYPES,
} from 'dashboard/components-next/message/constants';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import Media from 'dashboard/components-next/SharedAttachments/Media.vue';
import Files from 'dashboard/components-next/SharedAttachments/Files.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const MEDIA_PEEK_LIMIT = 12;
const FILES_PEEK_LIMIT = 6;

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const page = ref(0);
const attachmentsByContact = useMapGetter('contacts/getContactAttachments');

const attachments = computed(() =>
  page.value ? attachmentsByContact.value(route.params.contactId) : []
);

const hasContent = computed(() =>
  attachments.value.some(
    a => a.data_url && !NON_FILE_TYPES.includes(a.file_type)
  )
);

const mediaAttachments = computed(() =>
  attachments.value
    .filter(a => MEDIA_TYPES.includes(a.file_type) && a.data_url)
    .sort((a, b) => (b.created_at || 0) - (a.created_at || 0))
);

const showGallery = ref(false);
const selectedAttachment = ref(null);

const onMediaSelect = attachment => {
  selectedAttachment.value = attachment;
  showGallery.value = true;
};

const onFileSelect = attachment => {
  if (attachment.data_url) {
    window.open(attachment.data_url, '_blank', 'noopener,noreferrer');
  }
};

const onJumpToMessage = attachment => {
  if (!attachment.conversation_id || !attachment.message_id) return;
  const fromWorld =
    typeof route.query?.from_world === 'string'
      ? worldById(route.query.from_world)?.id
      : null;
  router.push({
    path: `/app/accounts/${route.params.accountId}/${isWorkspaceV2Path(route.path) ? 'v2/' : ''}conversations/${attachment.conversation_id}`,
    query: {
      messageId: attachment.message_id,
      ...(fromWorld ? { from_world: fromWorld } : {}),
    },
  });
};

const loadError = ref('');
const total = ref(0);
const loading = ref(false);
let requestId = 0;
let mounted = true;
const contextKey = computed(
  () => `${route.params.accountId || ''}:${route.params.contactId || ''}`
);

const loadMedia = async () => {
  if (loading.value) return;
  requestId += 1;
  const currentRequestId = requestId;
  const currentContext = contextKey.value;
  const isCurrent = () =>
    mounted &&
    currentRequestId === requestId &&
    currentContext === contextKey.value;

  loading.value = true;
  loadError.value = '';
  try {
    const data = await store.dispatch('contacts/fetchAttachments', {
      id: route.params.contactId,
      page: page.value + 1,
      append: page.value > 0,
      isCurrent,
    });
    if (!isCurrent()) return;
    page.value += 1;
    total.value = Number(data.meta?.total_count || 0);
  } catch {
    if (!isCurrent()) return;
    loadError.value = t('WORKSPACE_V2.CONTACT_DETAIL.LOAD_ERROR');
  } finally {
    if (isCurrent()) loading.value = false;
  }
};

watch(
  contextKey,
  () => {
    requestId += 1;
    page.value = 0;
    total.value = 0;
    loading.value = false;
    loadError.value = '';
    showGallery.value = false;
    selectedAttachment.value = null;
    loadMedia();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  mounted = false;
  requestId += 1;
});
</script>

<template>
  <div class="px-6">
    <div
      v-if="loadError"
      role="alert"
      class="rounded-xl border border-n-weak p-4 text-sm text-n-ruby-11"
    >
      <p>{{ loadError }}</p>
      <Button
        :label="t('WORKSPACE_V2.CONTACT_DETAIL.RETRY')"
        @click="loadMedia"
      />
    </div>
    <div v-if="loading && !page" class="flex justify-center p-3">
      <Spinner class="size-5" />
    </div>
    <p
      v-else-if="!hasContent && !loadError"
      class="p-3 text-sm text-center text-n-slate-11"
    >
      {{ t('CONVERSATION_SIDEBAR.SHARED_FILES.EMPTY') }}
    </p>
    <div v-else-if="hasContent" class="flex flex-col gap-5">
      <Media
        :attachments="attachments"
        :peek-limit="MEDIA_PEEK_LIMIT"
        show-jump-to-message
        @select="onMediaSelect"
        @jump-to-message="onJumpToMessage"
      />
      <Files
        :attachments="attachments"
        :peek-limit="FILES_PEEK_LIMIT"
        show-jump-to-message
        @select="onFileSelect"
        @jump-to-message="onJumpToMessage"
      />
    </div>
    <Button
      v-if="attachments.length < total && !loadError"
      :label="t('WORKSPACE_V2.CONTACT_DETAIL.LOAD_MORE')"
      :disabled="loading"
      :is-loading="loading"
      class="mt-4"
      @click="loadMedia"
    />
    <GalleryView
      v-if="showGallery && selectedAttachment"
      v-model:show="showGallery"
      :attachment="selectedAttachment"
      :all-attachments="mediaAttachments"
      auto-play
      @close="showGallery = false"
    />
  </div>
</template>
